import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ACTIONS, EVENTS, STATUS } from "react-joyride";
import type { EventData } from "react-joyride";
import {
  createWalkthroughSteps,
  type StepContext,
  type WalkthroughStep,
} from "../components/walkthrough/walkthroughSteps";
import { walkthroughSessionStore } from "../lib/walkthroughSessionStore";

interface UseWalkthroughOptions {
  navigateTo: (page: string, clientId?: string, params?: Record<string, unknown>) => void;
  janeClientId: string | null;
  onComplete?: () => void;
  onCancel?: () => void;
}

export function useWalkthrough({
  navigateTo,
  janeClientId,
  onComplete,
  onCancel,
}: UseWalkthroughOptions) {
  const [stepIndex, setStepIndex] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isStepReady, setIsStepReady] = useState(false);

  // Keep refs for current values (avoids stale closures in listeners)
  const stepIndexRef = useRef(0);
  const isRunningRef = useRef(false);
  const ctxRef = useRef<StepContext>({ navigateTo, janeClientId });
  ctxRef.current = { navigateTo, janeClientId };

  // MEMOIZE: createWalkthroughSteps runs on every render if not memoized,
  // producing a new `steps` array reference each time. Joyride re-initializes
  // when its `steps` prop reference changes, which causes it to mis-fire
  // STATUS.FINISHED during stepIndex transitions (e.g. floor-plan checkbox
  // click → stepIndex advance → rerender → new steps ref → Joyride resets →
  // tour flickers away mid-transition).
  const steps = useMemo(
    () => createWalkthroughSteps({ navigateTo, janeClientId }),
    [navigateTo, janeClientId]
  );
  const stepsRef = useRef<WalkthroughStep[]>(steps);
  stepsRef.current = steps;

  const start = useCallback(() => {
    setStepIndex(0);
    stepIndexRef.current = 0;
    setIsStepReady(false);
    setIsRunning(true);
    isRunningRef.current = true;
  }, []);

  const clearHighlights = useCallback(() => {
    document
      .querySelectorAll("[data-tour-active]")
      .forEach((el) => el.removeAttribute("data-tour-active"));
  }, []);

  const complete = useCallback(() => {
    setIsRunning(false);
    isRunningRef.current = false;
    setIsStepReady(false);
    setStepIndex(0);
    stepIndexRef.current = 0;
    clearHighlights();
    walkthroughSessionStore.setCompleted(true);
    onComplete?.();
  }, [clearHighlights, onComplete]);

  const cancel = useCallback(() => {
    setIsRunning(false);
    isRunningRef.current = false;
    setIsStepReady(false);
    setStepIndex(0);
    stepIndexRef.current = 0;
    clearHighlights();
    onCancel?.();
  }, [clearHighlights, onCancel]);

  const reset = useCallback(() => {
    setIsRunning(false);
    isRunningRef.current = false;
    setIsStepReady(false);
    setStepIndex(0);
    stepIndexRef.current = 0;
    clearHighlights();
  }, [clearHighlights]);

  // Manage data-tour-active attribute + click listeners for "click" steps
  useEffect(() => {
    if (!isRunning) return;

    const currentStep = stepsRef.current[stepIndex];
    if (!currentStep || typeof currentStep.target !== "string") return;
    setIsStepReady(false);
    const targetSelector = currentStep.target;
    const glowSelector =
      typeof currentStep.glowTarget === "string"
        ? currentStep.glowTarget
        : targetSelector;

    let cancelled = false;
    let glowEl: HTMLElement | null = null;
    let clickCleanup: (() => void) | null = null;

    const setupStep = async () => {
      try {
        await currentStep.prepare?.();
      } catch (error) {
        console.warn("Walkthrough step setup failed:", error);
      }

      if (cancelled || !isRunningRef.current) return;

      const targetEl = document.querySelector(targetSelector) as HTMLElement | null;
      glowEl = document.querySelector(glowSelector) as HTMLElement | null;
      if (!targetEl) {
        console.warn("Walkthrough target not ready:", targetSelector);
        return;
      }
      glowEl?.setAttribute("data-tour-active", "true");
      setIsStepReady(true);

      if (currentStep.stepType === "click" && targetEl) {
        const handler = () => {
          if (!isRunningRef.current) return;
          setIsStepReady(false);
          setTimeout(() => {
            const nextIndex = stepIndexRef.current + 1;
            if (nextIndex >= stepsRef.current.length) {
              complete();
            } else {
              stepIndexRef.current = nextIndex;
              setStepIndex(nextIndex);
            }
          }, 50);
        };

        targetEl.addEventListener("click", handler, {
          once: true,
          capture: true,
        });
        clickCleanup = () =>
          targetEl.removeEventListener("click", handler, { capture: true });
      }
    };

    void setupStep();

    return () => {
      cancelled = true;
      glowEl?.removeAttribute("data-tour-active");
      if (clickCleanup) clickCleanup();
    };
  }, [stepIndex, isRunning, complete]);

  // Joyride event handler — only matters for "explain" steps (Next/Back/Skip)
  const handleEvent = useCallback((data: EventData) => {
    const { action, index, status, type } = data;

    if (status === STATUS.SKIPPED) {
      cancel();
      return;
    }

    if (action === ACTIONS.CLOSE) {
      cancel();
      return;
    }

    if (status === STATUS.FINISHED) {
      complete();
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      // Only act on explain-type steps. Click-type steps are advanced by the
      // native click listener in the useEffect above; letting Joyride also
      // advance on STEP_AFTER causes a double-advance (skips the next step —
      // e.g. floor-plan-click jumps straight to preview, skipping email-fab).
      const currentStep = stepsRef.current[index];
      if (currentStep?.stepType === "click") {
        return;
      }
      if (action === ACTIONS.NEXT) {
        setIsStepReady(false);
        const nextIndex = index + 1;
        if (nextIndex >= stepsRef.current.length) {
          complete();
        } else {
          stepIndexRef.current = nextIndex;
          setStepIndex(nextIndex);
        }
      } else if (action === ACTIONS.PREV) {
        setIsStepReady(false);
        const prevIndex = Math.max(0, index - 1);
        stepIndexRef.current = prevIndex;
        setStepIndex(prevIndex);
      }
    }
  }, [cancel, complete]);

  return {
    stepIndex,
    isRunning,
    isStepReady,
    steps,
    start,
    complete,
    cancel,
    reset,
    handleEvent,
  };
}
